import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import { useT } from '../../i18n/LocaleContext';

const emptyForm = {
  plateNumber: '',
  name: '',
  capacity: '',
  driverName: '',
  driverPhone: '',
  active: true,
  notes: '',
};

export default function BusFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initial ? {
      plateNumber: initial.plateNumber || '',
      name: initial.name || '',
      capacity: initial.capacity != null ? String(initial.capacity) : '',
      driverName: initial.driverName || '',
      driverPhone: initial.driverPhone || '',
      active: initial.active !== false,
      notes: initial.notes || '',
    } : emptyForm);
    setErrors({});
  }, [isOpen, initial]);

  const set = (field) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.plateNumber.trim()) next.plateNumber = t('validation.required');
    const capacity = Number(form.capacity);
    if (!form.capacity || !Number.isInteger(capacity) || capacity < 1 || capacity > 150) {
      next.capacity = t('transport_page.capacity_invalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        plateNumber: form.plateNumber.trim().toUpperCase(),
        name: form.name.trim() || null,
        capacity: Number(form.capacity),
        driverName: form.driverName.trim() || null,
        driverPhone: form.driverPhone.trim() || null,
        active: form.active,
        notes: form.notes.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('transport_page.edit_bus') : t('transport_page.new_bus')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row form-row--2">
          <FormField label={t('transport_page.field_plate')} htmlFor="bus-plate" required error={errors.plateNumber}>
            <Input id="bus-plate" value={form.plateNumber} onChange={set('plateNumber')} invalid={Boolean(errors.plateNumber)} placeholder="RAD 123 A" maxLength={20} autoFocus />
          </FormField>
          <FormField label={t('transport_page.field_capacity')} htmlFor="bus-capacity" required error={errors.capacity}>
            <Input id="bus-capacity" type="number" min={1} max={150} value={form.capacity} onChange={set('capacity')} invalid={Boolean(errors.capacity)} />
          </FormField>
        </div>
        <FormField label={t('transport_page.field_bus_name')} htmlFor="bus-name" hint={t('transport_page.bus_name_hint')}>
          <Input id="bus-name" value={form.name} onChange={set('name')} maxLength={120} />
        </FormField>
        <div className="form-row form-row--2">
          <FormField label={t('transport_page.field_driver')} htmlFor="bus-driver" hint={t('common.optional')}>
            <Input id="bus-driver" value={form.driverName} onChange={set('driverName')} maxLength={120} />
          </FormField>
          <FormField label={t('transport_page.field_driver_phone')} htmlFor="bus-driver-phone" hint={t('common.optional')}>
            <Input id="bus-driver-phone" value={form.driverPhone} onChange={set('driverPhone')} maxLength={30} placeholder="+250 7..." />
          </FormField>
        </div>
        <FormField label={t('common.notes')} htmlFor="bus-notes" hint={t('common.optional')}>
          <Textarea id="bus-notes" value={form.notes} onChange={set('notes')} rows={2} />
        </FormField>
        <label className="checkbox-row">
          <input type="checkbox" checked={form.active} onChange={set('active')} />
          <span>{t('transport_page.active_bus')}</span>
        </label>
      </form>
    </Modal>
  );
}
